import React, { useRef, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { atom_tourState, atom_routeToStopLocation, atom_isNavigationMode } from '@app/shared/state/tour.atom';
import { ListMode } from '@app/shared/components/list-modes';
import { DesktopListContent } from '@app/shared/components/list-modes/DesktopListContent';
import { DesktopAddPlaceContent } from '@app/shared/components/list-modes/DesktopAddPlaceContent';
import { DesktopSavedContent } from '@app/shared/components/list-modes/DesktopSavedContent';
import { MobileListContent } from '@app/shared/components/list-modes/MobileListContent';
import { MobileSwiperContent } from '@app/shared/components/list-modes/MobileSwiperContent';
import { RouteListContent } from '@app/shared/components/list-modes/RouteListContent';
import { FilterContent } from '@app/shared/components/list-modes/FilterContent';
import { MobileNavigationContent } from '@app/shared/components/list-modes/MobileNavigationContent';
import { MobileAddPlaceContent } from '@app/shared/components/list-modes/add-a-place/MobileAddPlaceContent';
import { MobileCTAContent } from '@app/shared/components/list-modes/MobileCTAContent';
// DISABLED FOR PROD — re-enable for next release: planned-route feature
// import { UserPlannedTour } from '@app/shared/components/list-modes/UserPlannedTour';
// import { PlannedTourResults } from '@app/shared/components/list-modes/PlannedTourResults';
import { Notifications } from '@app/shared/components/Notifications';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { useRecoilValue, useRecoilCallback } from 'recoil';
import { atom_mapRef, atom_flyToSelectedStop } from '@app/shared/state/map-ref.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_activeCTANotification } from '@app/shared/state/notification.atom';
import { useRenderTracker } from '@app/shared/debug';

interface ListRenderProps {
  onToggleSmartAgent: () => void;
  mode?: 'mobile' | 'desktop';
}

interface ListRenderBaseProps {
  onToggleSmartAgent: () => void;
  mode?: 'mobile' | 'desktop';
  flyTo: (coords: [number, number], zoom: number) => void;
  mapRefObj: React.MutableRefObject<any>;
  flyToSelectedStop: (stopId: string) => void;
}

function ListRenderBase({ onToggleSmartAgent, mode = 'mobile', flyTo, mapRefObj, flyToSelectedStop }: ListRenderBaseProps) {
  useRenderTracker('ListRender');
  const { t, i18n } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const overlayContentRef = useRef<HTMLDivElement>(null);

  const tourState = useRecoilValue(atom_tourState);
  const routeToStopState = useRecoilValue(atom_routeToStopLocation);
  const isNavigationMode = useRecoilValue(atom_isNavigationMode);
  const globalListMode = useRecoilValue(atom_listModeState);
  const sideBarState = useRecoilValue(sideBarStateAtom);
  const activeCTA = useRecoilValue(atom_activeCTANotification);

  const isListMode = useMemo(() => (mode === 'desktop' ? true : !!tourState?.config?.['show_list']), [tourState, mode]);
  const isHebrew = useCallback(() => ((i18n as any).language === 'he' ? true : false), [i18n.language]);
  const isSavedMode = sideBarState === 'saved';

  const preventMapInteraction = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  // (State restoration moved to StateRestorer component)

  // Determine which list mode to render
  const getListMode = (): ListMode | null => {
    // Route-to-stop navigation takes priority over everything else when active.
    // Otherwise the early `globalListMode === null` return below would prevent the
    // route panel from reopening when the user clicks the navigate pill in MapFilters
    // (which clears listMode but expects the route panel to come back).
    if (routeToStopState && isNavigationMode) return 'route';
    if (globalListMode === null) return null;
    if (globalListMode === 'filter') return 'filter';
    if (globalListMode === 'mobileNavigation') return 'mobileNavigation';
    if (globalListMode === 'mobileAddPlace') return 'mobileAddPlace';
    if (globalListMode === 'desktopAddPlace') return 'desktopAddPlace';
    if (globalListMode === 'desktopNotifications') return 'desktopNotifications';
    // DISABLED FOR PROD — re-enable for next release: planned-route list modes
    // if (globalListMode === 'mobileUserPlannedTour') return 'mobileUserPlannedTour';
    // if (globalListMode === 'mobileUserPlannedTourResults') return 'mobileUserPlannedTourResults';
    if (mode === 'desktop') return 'desktop';
    if (isListMode) return 'mobileList';
    return 'mobileSwiper';
  };

  const listMode = getListMode();

  // Desktop modes - no persistent list needed, safe to return null
  if (listMode === null && mode === 'desktop') return null;

  // Mobile: never return null - hide with CSS to keep MobileListContent mounted
  if (listMode === 'desktop') {
    if (isSavedMode) {
      return (
        <DesktopSavedContent
          isHebrew={isHebrew}
          isListMode={isListMode}
          contentRef={contentRef}
          preventMapInteraction={preventMapInteraction}
          mapref={mapRefObj}
          flyToSelectedStop={flyToSelectedStop}
        />
      );
    }
    return (
      <DesktopListContent
        isHebrew={isHebrew}
        isListMode={isListMode}
        contentRef={contentRef}
        preventMapInteraction={preventMapInteraction}
        mapref={mapRefObj}
        flyToSelectedStop={flyToSelectedStop}
      />
    );
  }
  if (listMode === 'desktopAddPlace') {
    return <DesktopAddPlaceContent contentRef={contentRef} preventMapInteraction={preventMapInteraction} mapRef={mapRefObj} />;
  }
  if (listMode === 'desktopNotifications') {
    return <Notifications />;
  }

  // Mobile modes: base list + optional overlay
  const baseMode: ListMode = isListMode ? 'mobileList' : 'mobileSwiper';
  const isBaseMode = listMode === baseMode || listMode === null;
  const isOverlayMode = !isBaseMode && listMode !== 'route' && listMode !== 'mobileNavigation';

  const renderBaseList = () => {
    if (baseMode === 'mobileSwiper') {
      return (
        <>
          <MobileCTAContent />
          {!activeCTA && (
            <MobileSwiperContent isHebrew={isHebrew()} isListMode={isListMode} />
          )}
        </>
      );
    }
    return (
      <>
        <MobileCTAContent />
        {!activeCTA && (
          <MobileListContent
            isHebrew={isHebrew}
            isListMode={isListMode}
            contentRef={contentRef}
            preventMapInteraction={preventMapInteraction}
            flyTo={flyTo}
            onToggleSmartAgent={onToggleSmartAgent}
            mapref={mapRefObj}
            flyToSelectedStop={flyToSelectedStop}
          />
        )}
      </>
    );
  };

  const renderOverlay = () => {
    switch (listMode) {
      case 'filter':
        return <FilterContent contentRef={overlayContentRef} preventMapInteraction={preventMapInteraction} />;
      case 'mobileAddPlace':
        return <MobileAddPlaceContent contentRef={overlayContentRef} preventMapInteraction={preventMapInteraction} />;
      // ----- DISABLED FOR PROD — re-enable for next release: planned-route panels -----
      // case 'mobileUserPlannedTour':
      //   return <UserPlannedTour preventMapInteraction={preventMapInteraction} />;
      // case 'mobileUserPlannedTourResults':
      //   return <PlannedTourResults contentRef={overlayContentRef} preventMapInteraction={preventMapInteraction} />;
      // ----- /DISABLED FOR PROD -----
      default:
        return null;
    }
  };

  const renderReplacementMode = () => {
    switch (listMode) {
      case 'route':
        return <RouteListContent contentRef={contentRef} preventMapInteraction={preventMapInteraction} />;
      case 'mobileNavigation':
        return <MobileNavigationContent contentRef={contentRef} preventMapInteraction={preventMapInteraction} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Base list: always mounted, hidden via CSS when overlay or null mode is active */}
      <Box sx={{ display: isBaseMode && listMode !== null ? 'flex' : 'none', flexDirection: 'column', height: '100%', width: '100%' }}>
        {renderBaseList()}
      </Box>
      {/* Overlay: rendered on top, base list hidden underneath */}
      {isOverlayMode && renderOverlay()}
      {/* Route/navigation: fully replaces the list */}
      {!isOverlayMode && !isBaseMode && renderReplacementMode()}
    </Box>
  );
}

// Shared hook: reads mapRef and flyToSelectedStop on-demand
function useMapAtoms() {
  const getAtoms = useRecoilCallback(({ snapshot }) => () => ({
    mapRef: snapshot.getLoadable(atom_mapRef).contents as any,
    flyToSelectedStopFn: snapshot.getLoadable(atom_flyToSelectedStop).contents as ((stopId: string) => void) | null,
  }), []);

  const mapRefObj = useRef<any>(null);
  const mapRefValue = useRecoilValue(atom_mapRef);
  mapRefObj.current = mapRefValue;

  const flyTo = useCallback((coords: [number, number], zoom: number) => {
    const { mapRef } = getAtoms();
    mapRef?.flyTo(coords, zoom);
  }, [getAtoms]);

  const flyToSelectedStop = useCallback((stopId: string) => {
    const { flyToSelectedStopFn } = getAtoms();
    flyToSelectedStopFn?.(stopId);
  }, [getAtoms]);

  return { mapRefObj, flyTo, flyToSelectedStop };
}

export const ListRender = React.memo(function ListRender({ onToggleSmartAgent, mode = 'mobile' }: ListRenderProps) {
  const { mapRefObj, flyTo, flyToSelectedStop } = useMapAtoms();
  return <ListRenderBase
    onToggleSmartAgent={onToggleSmartAgent}
    mode={mode}
    flyTo={flyTo}
    mapRefObj={mapRefObj}
    flyToSelectedStop={flyToSelectedStop}
  />;
});
